import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { PerformanceData } from 'pages/hvareporting/debtcollection/CampaignPerformanceReport';
import CampaignPerformanceReportKPICard from 'components/cards/CampaignPerformanceReportKPICard';
import {
  faPhone,
  faPeopleGroup,
  faDollar,
  faHandHoldingDollar,
  faPeopleArrows,
  faComments
} from '@fortawesome/free-solid-svg-icons';

interface KPISectionProps {
  data: PerformanceData[];
}

const KPISection: React.FC<KPISectionProps> = ({ data }) => {
  // Función para calcular la media de un KPI
  const calculateAverage = (metricKey: string): number => {
    const sum = data.reduce((acc, item) => {
      const value = Number(item.campaigndata[metricKey]?.value) || 0;
      return acc + value;
    }, 0);
    return Math.round(sum / (data.length || 1));
  };

  // Función para calcular la media de un KPI
  const calculateOnedecimalAverage = (metricKey: string): number => {
    const sum = data.reduce((acc, item) => {
      const value = Number(item.campaigndata[metricKey]?.value) || 0;
      return acc + value;
    }, 0);
    return Math.round((sum / (data.length || 1)) * 10) / 10;
  };

  // Función para calcular la media de un KPI
  const calculatePercentageAverage = (metricKey: string): number => {
    const sum = data.reduce((acc, item) => {
      const value = Number(item.campaigndata[metricKey]?.value) || 0;
      return acc + value;
    }, 0);
    return Math.round((sum / (data.length || 1)) * 1000) / 1000;
  };

  // Función para preparar datos para el gráfico de evolución
  const prepareChartData = (
    metricKey: string
  ): { date: string; count: number }[] => {
    return data.map(item => ({
      date: item.date,
      count: Number(item.campaigndata[metricKey]?.value) || 0
    }));
  };

  // Función para preparar datos para el gráfico de evolución
  const prepareOnedecimalChartData = (
    metricKey: string
  ): { date: string; count: number }[] => {
    return data.map(item => ({
      date: item.date,
      // eslint-disable-next-line
      count: Math.round(Number(item.campaigndata[metricKey]?.value) * 10) / 10 || 0
    }));
  };

  // Función para preparar datos para el gráfico de evolución
  const preparePercentageChartData = (
    metricKey: string
  ): { date: string; count: number }[] => {
    return data.map(item => ({
      date: item.date,
      // eslint-disable-next-line
      count: Math.round(Number(item.campaigndata[metricKey]?.value) * 100000) / 1000 || 0
    }));
  };

  // Preparar datos para cada KPI
  const customersAvg = calculateAverage('Customers');
  const customersData = prepareChartData('Customers');

  const totalDebtAvg = calculateAverage('Total Debt');
  const totalDebtData = prepareChartData('Total Debt');

  const agreementsPercentageAvg = calculatePercentageAverage('% Agree.');
  const agreementsPercentageData = preparePercentageChartData('% Agree.');

  const callsPerCustomerAvg = calculateOnedecimalAverage('Calls/Cust.');
  const callsPerCustomerData = prepareOnedecimalChartData('Calls/Cust.');

  const answeredRateAvg = calculatePercentageAverage('% Answ.');
  const answeredRateData = preparePercentageChartData('% Answ.');

  const RPCRateAvg = calculatePercentageAverage('% RPC');
  const RPCRateData = preparePercentageChartData('% RPC');

  return (
    <Row className="gy-3 mb-4 justify-content-between bg-body-emphasis">
      <Col xs={12}>
        <Row className="g-3 pt-3 pb-3">
          <Col xs={12} xl={6} xxl={4}>
            <CampaignPerformanceReportKPICard
              icon={faPeopleGroup}
              color="primary"
              label="Average daily"
              title="Customers"
              valuetype="number"
              value={customersAvg}
              data={customersData}
            />
          </Col>
          <Col xs={12} xl={6} xxl={4}>
            <CampaignPerformanceReportKPICard
              icon={faDollar}
              color="primary"
              label="Average daily"
              title="Total Debt"
              valuetype="number"
              value={totalDebtAvg}
              data={totalDebtData}
            />
          </Col>
          <Col xs={12} xl={6} xxl={4}>
            <CampaignPerformanceReportKPICard
              icon={faHandHoldingDollar}
              color="primary"
              label="Average daily"
              title="Agreements rate"
              valuetype="percentage"
              value={agreementsPercentageAvg}
              data={agreementsPercentageData}
            />
          </Col>
          <Col xs={12} xl={6} xxl={4}>
            <CampaignPerformanceReportKPICard
              icon={faPhone}
              color="primary"
              label="Average daily"
              title="Calls per Customer"
              valuetype="onedecimal"
              value={callsPerCustomerAvg}
              data={callsPerCustomerData}
            />
          </Col>
          <Col xs={12} xl={6} xxl={4}>
            <CampaignPerformanceReportKPICard
              icon={faComments}
              color="primary"
              label="Average daily"
              title="Answered rate"
              valuetype="percentage"
              value={answeredRateAvg}
              data={answeredRateData}
            />
          </Col>
          <Col xs={12} xl={6} xxl={4}>
            <CampaignPerformanceReportKPICard
              icon={faPeopleArrows}
              color="primary"
              label="Average daily"
              title="RPC rate"
              valuetype="percentage"
              value={RPCRateAvg}
              data={RPCRateData}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default KPISection;
